import { useState, useEffect } from 'react';
import { Alert, Button, Input, notification, Progress, Typography, Tooltip } from 'antd';
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ConfettiExplosion from 'react-confetti-explosion';
import { motion } from 'framer-motion';
import { useMobileDeviceCheck } from 'src/common/hooks/useMobile';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import ResellerDashboard from '../ResellerDashboard';
import ResellerTour from '../ResellerTour/ResellerTour';
import { setTour } from '@/store/resellerTour/slice';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import { CustomModal } from '@/components/CustomModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResellerDashboardLink, UpdateResellerOnboardingStatus } from '@/api/reseller';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData, getResellerTrialStatus } from '@/store/reseller/selector';
import PricingCard from './PricingCard';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { setTrialStatus, updateReseller } from '@/store/reseller/slice';
import posthog from 'posthog-js';
import {
	CircleDollarSignIcon,
	CreditCardIcon,
	GlobeIcon,
	MailIcon,
	PaletteIcon,
	PhoneCallIcon,
	VideoIcon,
	CheckIcon,
	SmileIcon,
	FrameIcon,
	LockIcon,
	PlusCircleIcon,
} from 'lucide-react';
import './ResellerOnboarding.scss';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import LocalStorageUtility from 'src/utils/localStorage';
import { getSubscriptionInformation } from '@/api/user';

interface StepData {
	title: string;
	brief: string;
	icon: React.ReactNode;
	content: React.ReactNode;
}

const { Title } = Typography;

// Mapping object for step names
const STEP_EVENT_NAMES: Record<number, string> = {
	0: 'dashboard_tour',
	1: 'create_client_account',
	2: 'dashboard_customization',
	3: 'billing_setup',
	4: 'website_integration',
	5: 'email_notifications',
	6: 'support_call',
	7: 'pricing_tiers',
};

const ResellerOnboarding = () => {
	const resellerData = useAppSelector(getResellerData);
	const queryClient = useQueryClient();
	const { isMobile } = useMobileDeviceCheck();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [confettiTriggered, setConfettiTriggered] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);
	const [isCompleted, setIsCompleted] = useState(false);
	const [embedLinkModal, setEmbedLinkModal] = useState(false);
	const [localizedCurrentStep, setLocalizedCurrentStep] = useState<number | null>(null);

	const [highestCompletedStep, setHighestCompletedStep] = useState<number>(-1);

	const isResellerOnTrial = useAppSelector(getResellerTrialStatus);

	const { data: linkData } = useQuery({
		queryKey: ['getResellerDashboardLink', resellerData?.id],
		queryFn: () => ResellerDashboardLink(),
	});

	const isClientOnTrialMutation = useMutation({
		mutationKey: ['billingOverview', resellerData?.emailAddress],
		mutationFn: () => getSubscriptionInformation('Reseller'),
		onSuccess: (data) => {
			dispatch(setTrialStatus(!!data.data?.inboundAllowExtendTrial));
		},
		onError: () => {
			dispatch(setTrialStatus(false));
		},
	});

	useEffect(() => {
		isClientOnTrialMutation.mutate();
	}, [resellerData?.inboundSubscriptionId]);

	const updateResellerOnboardingStatusMutation = useMutation({
		mutationFn: (payload: {
			emailAddress: string;
			onboardingStep: number;
			isOnboardingComplete?: boolean;
			hasCompletedOnboarding?: boolean;
		}) => UpdateResellerOnboardingStatus(payload),
		onError: () => {
			notification.error({
				message: 'Failed to update progress',
				description: 'There was an error saving your progress. Please try again.',
			});
		},
	});

	useEffect(() => {
		if (!resellerData) {
			return;
		}

		if (resellerData?.onboardingStep) {
			const step = resellerData?.onboardingStep || 0;
			setHighestCompletedStep(step);
			setCurrentStep(step);
		}

		if (resellerData?.isOnboardingComplete) {
			setIsCompleted(true);
		}
	}, [resellerData]);

	// Add new state to track completed actions
	const [completedActions, setCompletedActions] = useState<Record<number, boolean>>({});

	useEffect(() => {
		const savedActions = LocalStorageUtility.getLocalData<Record<number, boolean>>(
			LocalStorageKeys.COMPLETED_ACTIONS,
		);
		if (savedActions) {
			setCompletedActions(savedActions);
		}
	}, []);

	const handleActionComplete = (step: number) => {
		const updatedActions = { ...completedActions, [step]: true };
		setCompletedActions(updatedActions);
		LocalStorageUtility.setLocalData(LocalStorageKeys.COMPLETED_ACTIONS, updatedActions);
	};

	const handleNext = async () => {
		const nextStep = async () => {
			if (currentStep < steps.length - 1) {
				const newStep = currentStep + 1;

				// Update the backend if this is a new highest step
				if (newStep > highestCompletedStep) {
					try {
						await new Promise((resolve, reject) => {
							updateResellerOnboardingStatusMutation.mutateAsync(
								{
									emailAddress: resellerData?.emailAddress as string,
									onboardingStep: newStep,
									isOnboardingComplete: false,
								},
								{
									onSuccess: () => {
										setHighestCompletedStep(newStep);
										setCurrentStep(newStep);

										dispatch(
											updateReseller({
												onboardingStep: newStep,
												isOnboardingComplete: false,
											}),
										);

										// track completion of current stage
										posthog.capture(
											`reseller_onboarding_${STEP_EVENT_NAMES[currentStep]}_completed`,
											{
												email_address: resellerData?.emailAddress,
											},
										);

										// track viewing of next stage
										posthog.capture(`reseller_onboarding_${STEP_EVENT_NAMES[newStep]}_viewed`, {
											email_address: resellerData?.emailAddress,
										});

										resolve(true);
									},
									onError: (error) => {
										reject(error);
									},
								},
							);
						});
					} catch (_err) {
						return;
					}
				} else {
					setCurrentStep(newStep);
				}
			}
		};

		setLocalizedCurrentStep(null);

		switch (currentStep) {
			case Object.keys(STEP_EVENT_NAMES).length - 1:
				if (resellerData?.isOnboardingComplete) {
					navigate(AppRoutes.RESELLER_PORTAL);
					return;
				}

				try {
					await new Promise((resolve, reject) => {
						updateResellerOnboardingStatusMutation.mutateAsync(
							{
								emailAddress: resellerData?.emailAddress as string,
								onboardingStep: currentStep + 1,
								isOnboardingComplete: true,
								hasCompletedOnboarding: true, // this indicates that the onboarding has been completed earlier but now the reseller is restarting it
							},
							{
								onSuccess: () => {
									setConfettiTriggered(true);
									setIsCompleted(true);

									dispatch(
										updateReseller({
											onboardingStep: currentStep,
											isOnboardingComplete: true,
											hasCompletedOnboarding: true,
										}),
									);

									// track completion of final stage
									posthog.capture(
										`reseller_onboarding_${STEP_EVENT_NAMES[currentStep]}_completed`,
										{
											email_address: resellerData?.emailAddress,
										},
									);

									// track overall completion
									posthog.capture('reseller_onboarding_completed', {
										email_address: resellerData?.emailAddress,
									});

									notification.info({
										message: 'Onboarding complete',
										description: 'Redirecting to the portal...',
										duration: 3,
									});
									setTimeout(() => {
										navigate(AppRoutes.RESELLER_PORTAL);
									}, 3000);
									resolve(true);
								},
								onError: (error) => {
									reject(error);
								},
							},
						);
					});
				} catch (_err) {
					return;
				}
				break;
			default:
				await nextStep();
				break;
		}
	};

	const skipAndContinue = async () => {
		const nextLocalizedStep = (localizedCurrentStep || 0) + 1;

		// if skipping ahead to a step that would be a new highest completed step
		// and the user is viewing a step they've already completed, update the backend
		if (nextLocalizedStep === currentStep + 1 && nextLocalizedStep > highestCompletedStep) {
			try {
				await new Promise((resolve, reject) => {
					updateResellerOnboardingStatusMutation.mutateAsync(
						{
							emailAddress: resellerData?.emailAddress as string,
							onboardingStep: nextLocalizedStep,
							isOnboardingComplete: nextLocalizedStep === steps.length - 1,
						},
						{
							onSuccess: () => {
								setHighestCompletedStep(nextLocalizedStep);
								setLocalizedCurrentStep(nextLocalizedStep);

								// Update global state
								dispatch(
									updateReseller({
										onboardingStep: nextLocalizedStep,
										isOnboardingComplete: nextLocalizedStep === steps.length - 1,
									}),
								);

								resolve(true);
							},
							onError: (error) => {
								reject(error);
							},
						},
					);
				});
			} catch (_err) {
				// Error is already handled by the mutation's onError callback
				return;
			}
		} else {
			setLocalizedCurrentStep(nextLocalizedStep);
		}
	};

	const getProgressDescription = (currentStep: number, totalSteps: number) => {
		const progress = (currentStep / totalSteps) * 100;

		if (progress === 0) {
			return "Let's get started with your reseller setup!";
		}
		if (progress <= 25) {
			return 'Great start! Taking your first steps...';
		}
		if (progress <= 50) {
			return 'Making good progress! Keep going...';
		}
		if (progress <= 75) {
			return 'Well done! Almost there...';
		}
		if (progress < 100) {
			return "Final steps! You're nearly finished...";
		}
		return 'Setup complete! Your reseller account is ready!';
	};

	const handleCopyIframe = async (route: string) => {
		if (linkData?.link) {
			try {
				const iframeUrl = `${import.meta.env.VITE_APP_WIHTELABEL_URL}${route}?code=${linkData.link}`;
				const iframeString = `<iframe src="${iframeUrl}" width="100%" height="100%" frameBorder="0" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        border: 'none',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        zIndex: 999999,
      }}></iframe>`;

				await navigator.clipboard.writeText(iframeString);
				notification.success({ message: 'iframe copied successfully' });
			} catch {
				notification.error({ message: 'Failed to copy iframe' });
			}
		} else {
			notification.error({ message: 'No link available to copy' });
		}
	};

	const stepsData: StepData[] = [
		{
			title: 'Platform Tour',
			brief: 'Explore your dashboard',
			icon: <VideoIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Let's explore your{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								AI Dashboard
							</span>
						</h2>
						<p className="text-lg text-gray-600">
							Take a quick tour to discover all the powerful features at your fingertips.
						</p>
					</div>

					<div className="relative flex items-center justify-center">
						<button
							onClick={() => {
								dispatch(
									setTour({ open: true, currentStep: 0, returnTo: AppRoutes.RESELLER_ONBOARDING }),
								);
								handleActionComplete(0);
							}}
							className={`flex items-center gap-2 bg-[#E6EDFB] text-blue-600 font-semibold rounded-xl text-lg border border-gray-300 px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[0] ? 'has-ping-large' : ''} relative overflow-hidden`}
						>
							<VideoIcon className="w-4 h-4" />
							<span>Watch the tour</span>
						</button>
					</div>
				</div>
			),
		},
		{
			title: 'Create Client Account',
			brief: 'Set up your first client',
			icon: <PlusCircleIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Set Up Your First{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								Client Account
							</span>
						</h2>
						<p className="text-lg text-gray-600">
							Get started by creating a trial account to test the capabilities of the portal at its
							best. Every trial account includes 15 free test calls—on us!
						</p>
					</div>

					<div className="flex items-center justify-center gap-4">
						<button
							onClick={() => {
								navigate(AppRoutes.RESELLER_PORTAL + '?create-trial-client-modal=true');
								handleActionComplete(1);
							}}
							className={`flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[1] ? 'has-ping-large' : ''} relative overflow-hidden`}
						>
							<PlusCircleIcon className="w-5 h-5" />
							<span>Create Test Account</span>
						</button>
					</div>
				</div>
			),
		},
		{
			title: 'Personalization',
			brief: 'Make it yours',
			icon: <PaletteIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Create your{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								signature experience
							</span>
						</h2>
						<p className="text-lg text-gray-600">
							Customize every aspect of your clients' experience, from branding to UI elements.
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
						<div className="bg-[#F5F7FF] rounded-2xl p-6 flex flex-col items-center text-center">
							<div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
								<SmileIcon className="w-6 h-6 text-blue-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">Brand Identity</h3>
							<p className="text-gray-600">Upload your logo and brand assets</p>
						</div>

						<div className="bg-[#FFF5F5] rounded-2xl p-6 flex flex-col items-center text-center">
							<div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
								<PaletteIcon className="w-6 h-6 text-blue-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">Color Theme</h3>
							<p className="text-gray-600">Define your color palette and theme</p>
						</div>

						<div className="bg-[#F5FFFA] rounded-2xl p-6 flex flex-col items-center text-center">
							<div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-md">
								<CreditCardIcon className="w-6 h-6 text-blue-600" />
							</div>
							<h3 className="text-xl font-semibold mb-2">UI Elements</h3>
							<p className="text-gray-600">Customize buttons, features and policies</p>
						</div>
					</div>

					<div className="flex justify-center mt-8">
						<button
							onClick={() => {
								navigate(AppRoutes.RESELLER_BRANDING);
								handleActionComplete(2);
							}}
							className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[2] ? 'has-ping-large' : ''} relative overflow-hidden`}
						>
							<PaletteIcon className="w-5 h-5" />
							<span>Start customizing</span>
						</button>
					</div>
				</div>
			),
		},
		{
			title: 'Get Paid',
			brief: 'Set up your billing',
			icon: <CreditCardIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Decide how you{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								bill your clients
							</span>
						</h2>
						<p className="text-lg text-gray-600">
							Choose your preferred billing method to manage client payments.
						</p>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									1
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Direct Billing</h3>
									<p className="text-gray-600">
										Invoice your clients directly, just like you are doing now.
									</p>
								</div>
							</div>
						</div>

						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									2
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Stripe Integration</h3>
									<p className="text-gray-600">
										Connect Stripe to automatically bill clients through our platform.
									</p>
								</div>
							</div>
						</div>
					</div>

					<p className="text-gray-600 text-center">
						Choose the billing method that works best for you.
					</p>

					<div className="flex justify-center mt-8">
						<button
							onClick={() => {
								navigate(AppRoutes.RESELLER_PLAN_CREATION);
								handleActionComplete(3);
							}}
							className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[3] ? 'has-ping-large' : ''} relative overflow-hidden`}
						>
							<CreditCardIcon className="w-5 h-5" />
							<span>Connect Stripe</span>
						</button>
					</div>
				</div>
			),
		},
		{
			title: 'Plug in Website',
			brief: 'Add it to your site',
			icon: <GlobeIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Let your clients{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								register and login{' '}
							</span>
							on your website
						</h2>
						<p className="text-lg text-gray-600">
							You can integrate our service with your website in two ways:
						</p>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									1
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Iframe Embed</h3>
									<p className="text-gray-600">
										Easily embed our Iframe directly into your website for a seamless experience.
									</p>
								</div>
							</div>
						</div>

						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									2
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">CNAME Connection</h3>
									<p className="text-gray-600">
										Connect your website using a CNAME record for a more customized experience.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="flex flex-col items-center">
						<p className="text-base text-gray-600">
							Select your preffered connection method below:
						</p>
						<div className="flex gap-4">
							<button
								onClick={() => {
									setEmbedLinkModal(true);
									handleActionComplete(4);
								}}
								className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[4] ? 'has-ping-large' : ''} relative overflow-hidden`}
							>
								<FrameIcon className="w-5 h-5" />
								<span>Iframe Embed</span>
							</button>

							<button
								onClick={() => {
									navigate(AppRoutes.RESELLER_CUSTOM_DOMAIN);
									handleActionComplete(4);
								}}
								className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[4] ? 'has-ping-large' : ''} relative overflow-hidden`}
							>
								<GlobeIcon className="w-5 h-5" />
								<span>CNAME Connection</span>
							</button>
						</div>
					</div>
				</div>
			),
		},
		{
			title: 'Email Sending Options',
			brief: 'Configure notifications',
			icon: <MailIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Decide how you send{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								notifications{' '}
							</span>
							to your clients
						</h2>
						<p className="text-lg text-gray-600">
							There are two ways to send email notifications to your clients:
						</p>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									1
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Gray-Labeled</h3>
									<p className="text-gray-600">
										Emails are sent from an unbranded domain: notifications@mail.aireceptionist.org
									</p>
								</div>
							</div>
						</div>

						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									2
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Bring your own Mailgun</h3>
									<p className="text-gray-600">
										Use your own Mailgun account to send emails from your own email addresses.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="flex flex-col items-center">
						<p className="text-base text-gray-600">
							Connect your mailgun if you'd like to send emails from your own email addresses.
						</p>
						<div className="flex justify-center mt-8">
							<button
								onClick={() => {
									navigate(AppRoutes.RESELLER_EMAIL_CONFIG);
									handleActionComplete(5);
								}}
								className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[5] ? 'has-ping-large' : ''} relative overflow-hidden`}
							>
								<MailIcon className="w-5 h-5" />
								<span>Connect Mailgun</span>
							</button>
						</div>
					</div>
				</div>
			),
		},
		{
			title: 'Book Onboarding Call',
			brief: 'Connect with our team',
			icon: <PhoneCallIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							We are in this{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								together
							</span>
							. Book your first onboarding call
						</h2>
						<p className="text-lg text-gray-600">
							We're always here to help! Get started by booking your first onboarding call:
						</p>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									1
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Technical Call</h3>
									<p className="text-gray-600">
										Discuss AI and system-related questions with our experts.
									</p>
								</div>
							</div>
						</div>

						<div className="bg-white rounded-2xl p-4 border border-gray-200 hover:border-blue-200 transition-all duration-200">
							<div className="flex items-start gap-4">
								<div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-semibold">
									2
								</div>
								<div>
									<h3 className="text-xl font-semibold mb-2">Business Call</h3>
									<p className="text-gray-600">
										Go over business-related topics to ensure a smooth setup.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="flex flex-col items-center">
						<p className="text-base text-gray-600">
							Don't worry, you will always be able to reach us through the Contact Support tab in
							your Admin Dashboard.
						</p>
						<div className="flex gap-4">
							<button
								onClick={() => {
									window.open(
										'https://calendly.com/d/cmq2-bzn-bth/ai-front-desk-onboarding-setup-help',
										'_blank',
									);
									handleActionComplete(6);
								}}
								className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[6] ? 'has-ping-large' : ''} relative overflow-hidden`}
							>
								<PhoneCallIcon className="w-5 h-5" />
								<span>Technical Call</span>
							</button>
							{isResellerOnTrial || resellerData?.totalInboundAgents < 7 ? (
								<Tooltip title="You will be able to book a business call once you are out of your trial period and you have 7 or more clients.">
									<button
										disabled
										className="flex items-center gap-2 text-white font-semibold rounded-xl text-lg px-8 py-5 bg-gray-400 cursor-not-allowed opacity-75"
									>
										<LockIcon className="w-5 h-5" />
										<span>Business Call</span>
									</button>
								</Tooltip>
							) : (
								<button
									onClick={() => {
										window.open(
											'https://calendly.com/d/cqb8-y7w-74c/ai-front-desk-white-label-discovery-call',
											'_blank',
										);
										handleActionComplete(6);
									}}
									className={`flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-xl text-lg px-8 py-5 hover:transform hover:scale-110 transition-all duration-300 ${!completedActions[6] ? 'has-ping-large' : ''} relative overflow-hidden`}
								>
									<PhoneCallIcon className="w-5 h-5" />
									<span>Business Call</span>
								</button>
							)}
						</div>
					</div>
				</div>
			),
		},
		{
			title: 'White Label Program',
			brief: 'Access exclusive perks',
			icon: <CircleDollarSignIcon className="w-4 h-4" />,
			content: (
				<div className="space-y-8">
					<div>
						<h2 className="text-4xl font-semibold mb-2">
							Scaling perks with{' '}
							<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-blue-700">
								My AI Front Desk
							</span>{' '}
							white label program!
						</h2>
						<p className="text-lg text-gray-600">
							As you grow with us, we want to provide the best support possible. That's why we're
							introducing White Label Program Tiers. We want to reward our best resellers and offer
							various perks based on how many clients you bring in.
						</p>
					</div>

					<Alert
						message={
							<div className="text-blue-700">
								You can always check your progress in your admin dashboard under{' '}
								<strong>{'My Receptionists --> View Reseller Progress'}</strong>
							</div>
						}
						type="info"
						showIcon
						icon={<InfoCircleOutlined />}
					/>

					<div className="mt-8">
						<h3 className="text-xl font-semibold text-center mb-6">Let's go over the Tiers:</h3>

						<div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
							<PricingCard
								description="1-19 seats"
								title="Lite"
								features={['Dedicated CSM', 'Priority Support']}
								bgClasses="bg-gradient-to-r from-gray-50 to-gray-200"
								iconClass="bg-gray-400"
								textClass="text-gray-700"
								darkBg="bg-gray-500"
							/>

							<PricingCard
								description="20-49 seats"
								title="Bronze"
								features={[
									'Private Slack Channel',
									'Weekly Call with WL Program Director',
									'Priority Integration Request',
								]}
								bgClasses="bg-gradient-to-r from-amber-50 to-amber-500"
								iconClass="bg-amber-600"
								textClass="text-amber-700"
								darkBg="bg-amber-600"
							/>

							<PricingCard
								description="50-99 seats"
								title="Silver"
								features={['Priority Feature Request', 'Lower Per Receptionist Price']}
								bgClasses="bg-gradient-to-r from-gray-200 to-gray-400"
								iconClass="bg-gray-600"
								textClass="text-gray-800"
								darkBg="bg-gray-700"
							/>

							<PricingCard
								description="100-149 seats"
								title="Gold"
								features={['Beta Access to New Integrations', 'First 20% Access to New Features']}
								bgClasses="bg-gradient-to-r from-yellow-50 to-yellow-400"
								iconClass="bg-yellow-500"
								textClass="text-yellow-700"
								darkBg="bg-yellow-500"
							/>

							<PricingCard
								description="150+ seats"
								title="Platinum"
								features={['Monthly Founder Calls', 'Lower Per Minute Rates']}
								bgClasses="bg-gradient-to-r from-blue-50 to-blue-200"
								iconClass="bg-blue-600"
								textClass="text-blue-700"
								darkBg="bg-blue-500"
							/>
						</div>
					</div>
				</div>
			),
		},
	];

	const steps = stepsData.map((step, index) => {
		// if onboarding is complete, mark all steps as completed
		if (resellerData?.isOnboardingComplete) {
			return {
				...step,
				completed: true,
			};
		}

		const completed = index < currentStep;
		return {
			...step,
			completed,
		};
	});

	const stepPercentage =
		isCompleted || resellerData?.isOnboardingComplete
			? 100
			: Math.min(Math.ceil((currentStep / steps.length) * 100), 100);

	const handleStepClick = (index: number) => {
		// if onboarding is complete, don't allow navigation
		if (resellerData?.isOnboardingComplete) {
			return;
		}

		if (index > currentStep) {
			return;
		}
		setLocalizedCurrentStep(index);
	};

	const renderStepper = () => {
		if (isMobile) {
			return (
				<div className="flex flex-col items-center">
					<div className="flex justify-center mb-4 space-x-2">
						{steps.map((step, index) => (
							<div
								key={index}
								className={`min-w-8 min-h-8 w-8 h-8 flex items-center justify-center rounded-full text-white relative ${
									step.completed
										? 'bg-green-500'
										: index === currentStep
											? 'bg-blue-500'
											: 'bg-gray-300'
								}`}
							>
								{step.completed ? <CheckIcon className="w-4 h-4" /> : step.icon}
								{index === currentStep && !step.completed && (
									<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
								)}
							</div>
						))}
					</div>
					<p className="text-sm text-gray-600">
						{getProgressDescription(currentStep, steps.length - 1)}
					</p>
				</div>
			);
		}

		return (
			<div className="w-1/4 bg-white border-r overflow-y-auto rounded-lg h-fit shadow-lg border">
				<header className="p-4 flex flex-col items-center justify-center gap-1 border-b">
					<Progress
						type="circle"
						percent={stepPercentage}
						width={75}
						strokeColor={stepPercentage > 80 ? '#52C41A' : '#3B82F6'}
						trailColor="#DBEAFE"
						strokeWidth={8}
						format={() => (
							<span
								className={`font-medium ${stepPercentage > 80 ? 'text-[#52C41A]' : 'text-[#3B82F6]'}`}
							>
								{stepPercentage}%
							</span>
						)}
					/>
					<span className="opacity-50 text-xs">Setup Progress</span>
				</header>
				<div className="p-4">
					{steps.map((step, index) => {
						const stepCompleted = step.completed;
						const isActive =
							(index === currentStep && !stepCompleted) || index === localizedCurrentStep;

						return (
							<div
								key={index}
								onClick={() => handleStepClick(index)}
								className={`flex items-center p-3 my-2 space-x-4 rounded-lg cursor-pointer transition-all duration-200 ${
									isActive ? 'bg-[#E6EDFB]' : 'hover:bg-gray-50'
								}`}
							>
								<div className="relative">
									<div
										className={`flex items-center justify-center min-w-8 min-h-8 w-8 h-8 rounded-full 
                      ${stepCompleted ? 'bg-green-100' : isActive ? 'bg-blue-100' : 'bg-gray-100'}
                      ${stepCompleted ? 'text-green-600' : isActive ? 'text-blue-600' : 'text-gray-400'}`}
									>
										{stepCompleted ? <CheckIcon className="w-4 h-4" /> : step.icon}
										{isActive && !stepCompleted && (
											<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-20" />
										)}
									</div>
								</div>
								<div className="flex flex-col flex-1">
									<span
										className={`text-sm font-medium ${isActive ? 'text-blue-600' : stepCompleted ? 'text-gray-600' : 'text-gray-400'}`}
									>
										{step.title}
									</span>
									<span className="text-xs text-gray-400">{step.brief}</span>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const activeStep = localizedCurrentStep !== null ? localizedCurrentStep : currentStep;
	const isLastStep = activeStep === steps.length - 1;

	const resetOnboarding = async () => {
		await updateResellerOnboardingStatusMutation.mutateAsync({
			emailAddress: resellerData.emailAddress as string,
			onboardingStep: 0,
			isOnboardingComplete: false,
		});

		queryClient.invalidateQueries({ queryKey: ['reseller'] });

		setCurrentStep(0);
		setLocalizedCurrentStep(0);
		setCompletedActions({});
		setIsCompleted(false);
		setConfettiTriggered(false);
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.ONBOARDING}>
			<ResellerTour />

			<CustomModal
				title={null}
				isModalOpen={embedLinkModal}
				confirmAction={() => {
					setEmbedLinkModal(false);
				}}
				cancelAction={() => {
					setEmbedLinkModal(false);
				}}
			>
				<Title level={4}>Embed iframes for login/register</Title>
				<p className="text-sm">
					Copy the below iframe code and paste it in your website to embed login/register.
				</p>

				<Typography.Text strong>For Login</Typography.Text>

				<Input
					value={`<iframe src=${import.meta.env.VITE_APP_WIHTELABEL_URL + AppRoutes.UNBRANDED_DASHBOARD + '?code=' + linkData?.link} width="100%" height="100%" frameBorder="0"></iframe>`}
					readOnly
					suffix={
						<CopyOutlined
							className="cursor-pointer text-gray-500 hover:text-gray-700"
							onClick={() => handleCopyIframe(AppRoutes.UNBRANDED_DASHBOARD)}
						/>
					}
				/>
				<Typography.Text strong>For Register</Typography.Text>
				<Input
					value={`<iframe src=${import.meta.env.VITE_APP_WIHTELABEL_URL + AppRoutes.GENERIC_DOMAIN_REGISTER + '?code=' + linkData?.link} width="100%" height="100%" frameBorder="0"></iframe>`}
					readOnly
					suffix={
						<CopyOutlined
							className="cursor-pointer text-gray-500 hover:text-gray-700"
							onClick={() => handleCopyIframe(AppRoutes.GENERIC_DOMAIN_REGISTER)}
						/>
					}
				/>
			</CustomModal>
			<div
				className={`py-6 flex w-full relative ${isMobile ? 'flex-col items-center justify-center space-y-2' : 'space-x-10'}`}
			>
				{confettiTriggered && <ConfettiExplosion />}

				{/* Sidebar */}
				{renderStepper()}

				{/* Main Content */}
				<div
					className={`py-4 px-6 md:py-10 md:px-12 bg-gray-50 rounded-lg h-fit shadow-lg border ${isMobile ? 'w-full' : 'w-3/4'}`}
				>
					{isCompleted || resellerData?.isOnboardingComplete ? (
						<>
							<div className="text-center">
								<motion.h1
									className="text-4xl font-bold mb-0 leading-relaxed bg-gradient-to-r from-blue-500 to-blue-700 text-transparent bg-clip-text"
									initial={{ y: -20, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.2 }}
								>
									Setup Complete!
								</motion.h1>
								<motion.p
									className="text-lg text-gray-600 mb-6 text-center"
									initial={{ y: -10, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.3 }}
								>
									Awesome! You've completed all the necessary steps. Your reseller account is now
									ready to use.
								</motion.p>
								{!confettiTriggered && resellerData?.isOnboardingComplete && (
									<div className="flex justify-center items-center gap-2">
										<Button
											type="primary"
											size="large"
											onClick={() => navigate(AppRoutes.RESELLER_PORTAL)}
										>
											Go to Dashboard
										</Button>
										<Button type="default" size="large" onClick={resetOnboarding}>
											Start Again
										</Button>
									</div>
								)}
							</div>
						</>
					) : (
						<>
							<div>
								<motion.div
									initial={{ y: -10, opacity: 0 }}
									animate={{ y: 0, opacity: 1 }}
									transition={{ delay: 0.3 }}
								>
									{
										steps[localizedCurrentStep === null ? currentStep : localizedCurrentStep]
											?.content
									}
								</motion.div>
							</div>
							<div className="mt-8 flex justify-end items-center gap-2">
								<button
									onClick={
										localizedCurrentStep !== null && localizedCurrentStep !== currentStep
											? skipAndContinue
											: handleNext
									}
									disabled={updateResellerOnboardingStatusMutation.isPending}
									className={`relative overflow-hidden text-base rounded-lg px-8 py-2 hover:transform hover:-translate-y-0.5 transition-all duration-200 font-medium flex items-center gap-2 ${
										isLastStep || completedActions[activeStep] ? 'has-ping-large' : ''
									} ${
										isLastStep || completedActions[activeStep]
											? 'bg-blue-600 text-white hover:bg-blue-700'
											: 'text-gray-700 hover:bg-gray-100'
									} ${updateResellerOnboardingStatusMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
								>
									<span>
										{isLastStep
											? 'Finish'
											: completedActions[activeStep]
												? 'Mark as completed'
												: "I'll do this later"}
									</span>
									{(isLastStep || completedActions[activeStep]) && (
										<CheckIcon className="w-4 h-4" />
									)}
								</button>
							</div>
						</>
					)}
				</div>
			</div>
		</ResellerDashboard>
	);
};

export default ResellerOnboarding;
